import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export function PageHero({ eyebrow, title }) {
  // BITS-inspired hero: deep navy with subtle gold accent ribbon
  return (
    <section
      className="relative bg-[#001F3F] text-white py-16 md:py-24 overflow-hidden"
      data-testid="roe-page-hero"
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 78% 22%, #D4A017 0%, transparent 38%), radial-gradient(circle at 12% 82%, #0A2540 0%, transparent 40%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(135deg, transparent 50%, rgba(212,160,23,0.06) 50%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6">
        {eyebrow && (
          <div className="text-[10px] tracking-[0.45em] uppercase text-[#D4A017] font-bold">
            {eyebrow}
          </div>
        )}
        <h1
          className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {title}
        </h1>
        <div className="mt-6 h-[3px] w-20 bg-[#D4A017]" />
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-6 py-12 md:py-16 ${className}`}>{children}</section>
  );
}

export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="roe-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={22} />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIdx((idx - 1 + photos.length) % photos.length);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIdx((idx + 1) % photos.length);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={cur.image_url || cur.url}
          alt={cur.caption || ""}
          className="max-w-full max-h-[80vh] object-contain"
        />
        {cur.caption && (
          <figcaption className="mt-3 text-center text-white/90 text-sm">
            {cur.caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function slugify(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}


// Lightweight renderer for institute Vision/Mission/About content (HTML or plain text).
export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml
    ? content
    : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

export const STATIC_CONTENT = {
  about_gsm: `Gramaudyogik Shikshan Mandal, popularly known as GSM, is the parent educational trust established in 1975 under the Bombay Public Trust Act and also registered under the Societies Registration Act. Over the decades, GSM has emerged as a reputed educational organization committed to providing quality technical and professional education in India. The organization has grown organically across three major regions of India — Maharashtra (Western India), Delhi (National Capital Region), and Uttar Pradesh (Northern India). Presently, GSM manages a network of educational institutions and learning centres at multiple locations across the country.`,
  about_mit: `MIT Polytechnic is one of the flagship institutions under GSM. Established in 1983 with the approval of the All India Council for Technical Education (AICTE), MIT Polytechnic was founded to conduct full-time diploma courses in engineering and technology. MIT is a group of institutions offering education in Polytechnic, Engineering, Technology, and Architecture. The institute is dedicated to developing innovative technologies and nurturing technically competent, creative, and socially responsible professionals capable of addressing complex global challenges. The institution has established a strong academic presence across Maharashtra, Uttar Pradesh, and New Delhi. With standardized academic and administrative processes, MIT achieved ISO 9001:2000 certification, reflecting its commitment to quality education and continuous improvement. MIT Polytechnic takes pride in its strong alumni network of more than 2,000 MITians who are contributing successfully across industries, entrepreneurship, research, and educational institutions worldwide.`,
  vision: `To become a globally recognized technical institution in skill and competency development for acquiring latest requirements of industry by keeping social and environmental awareness.`,
  mission: `Institute aims at imparting student centric quality education through teaching-learning process and training for diploma students so that they will be able to start their own business, employable at the best position in the field of engineering & technology, achieve higher education capability to become a best citizen.`,
  contact: {
    title: "Marathwada Institute of Technology, Polytechnic (DTE Code: 2224)",
    address:
      "MIT Campus, Beed Bypass Road, Satara Village Road, Chhatrapati Sambhajinagar – 431010, Maharashtra, INDIA",
    phones: ["0240-2375333", "0240-2375303", "0240-2375375"],
    fax: "0240-2376230",
    email: "principal.mitp@mit.asia",
    website: "http://polyrotegaon.mit.asia",
    head_office: {
      title: "MIT, Head Office",
      address:
        "MIT, Beed Bypass Road, Satara Village Road, Chhatrapati Sambhajinagar – 431010, Maharashtra State, INDIA",
      phones: ["+91 240 2375375"],
      fax: "+91 240 2375275, 2376154",
      email: "info@mit.asia",
      website: "www.mit.asia",
    },
  },
};
